import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";

/**
 * Sidebar of the driver and company areas. From lg up it is the usual sticky
 * column; on phones and tablets it slides in from the left (opened by the
 * header's menu button) over a dimmed page, and closes on a link, the
 * backdrop, the close button or Escape.
 */
export default function SideNav({
  open,
  onClose,
  label,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** aria-label of the <nav> */
  label: string;
  /** Shown at the top of the panel on phones (company name, "Driver area") */
  title?: string;
  children: ReactNode;
}) {
  const t = useT();
  const { pathname } = useLocation();
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  // A link was followed: the panel closes
  useEffect(() => {
    closeRef.current();
  }, [pathname]);

  // While open: Escape closes it and the page behind doesn't scroll
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeRef.current();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={cn("fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 lg:hidden", open ? "opacity-100" : "pointer-events-none opacity-0")}
      />
      <nav
        id="side-nav"
        aria-label={label}
        className={cn(
          // Phones: off-canvas panel (invisible when closed, so links can't be tabbed to)
          "fixed inset-y-0 left-0 z-50 flex w-[min(288px,85vw)] flex-col gap-0.5 overflow-y-auto bg-surface px-3 pb-6 shadow-2xl transition-[transform,visibility] duration-200",
          open ? "visible translate-x-0" : "invisible -translate-x-full",
          // lg+: the regular sidebar column
          "lg:visible lg:sticky lg:top-14 lg:z-auto lg:max-h-[calc(100vh-56px)] lg:w-auto lg:translate-x-0 lg:py-6 lg:shadow-none",
        )}
      >
        <div className="sticky top-0 z-10 -mx-3 mb-2 flex h-14 items-center justify-between gap-2 border-b border-line-strong bg-surface px-4 lg:hidden">
          <span className="truncate text-sm font-semibold text-ink">{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.common.close}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded border-0 bg-white text-ink"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {children}
      </nav>
    </>
  );
}

/** The header button that opens the SideNav (phones and tablets only). */
export function SideNavButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-controls="side-nav"
      aria-label={t.header.menu}
      className="-ml-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-ink hover:bg-surface lg:hidden"
    >
      <Menu size={20} aria-hidden="true" />
    </button>
  );
}
